import React, { useEffect, useState, Suspense } from "react";
import { supabase } from "./integrations/supabase/client";
import { Toaster } from "./components/ui/toaster";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RequireAuth } from "./pages/RequireAuth";
import { DashboardLayout } from "./pages/DashboardLayout";
import { Loading } from "./components/shared/ui/Loading";
import type { Session } from "@supabase/supabase-js";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { logErrorToConsole } from "./utils/errorHandlerUtils";
import { RequirePermission } from "./components/auth/RequirePermission";
import { ErrorHandlerProvider } from "./contexts/ErrorHandlerContext";
import { FEATURES } from "./config/featureFlags";
import { offlineQueryPersister } from "./lib/queryPersistence";
import { unsubscribeLocalWebPush } from "./features/pwa/services/webPush.service";
import { routeLoaders } from "./lib/routeLoaders";

// Lazy load de los componentes de página. Los cargadores viven en
// lib/routeLoaders para que la navegación pueda precargarlos al pasar por un
// enlace (mismo import dinámico ⇒ mismo chunk).
const OverviewPage = React.lazy(routeLoaders.overview);
const NetworkPage = React.lazy(routeLoaders.network);
const ClimatePage = React.lazy(routeLoaders.climate);
const AnalysisPage = React.lazy(routeLoaders.analysis);
const RegistrationPage = React.lazy(routeLoaders.registration);
const RulesPage = React.lazy(routeLoaders.rules);
const SystemSettingsPage = React.lazy(routeLoaders.systemSettings);
const UserManagementPage = React.lazy(routeLoaders.usersManagement);
const ProfilePage = React.lazy(routeLoaders.profile);
const IntegrationsPage = React.lazy(routeLoaders.integrations);
const IntegrationHelpPage = React.lazy(() => import("./pages/IntegrationHelpPage"));
const IntegrationLabPage = React.lazy(() => import("./pages/IntegrationLabPage"));
const CropsPage = React.lazy(routeLoaders.crops);
const ImageAnalysisPage = React.lazy(routeLoaders.imageAnalysis);
const DevicesPage = React.lazy(routeLoaders.devices);
const ActuatorsPage = React.lazy(routeLoaders.actuators);
const AlertsPage = React.lazy(routeLoaders.alerts);
// El login solo hace falta SIN sesión: fuera del chunk de entrada, el usuario
// que ya entró no descarga formularios ni el fondo animado de la portada.
const Auth = React.lazy(() =>
  import("./features/auth/components/Auth").then((module) => ({ default: module.Auth }))
);
const NotFound = React.lazy(() => import("./pages/NotFound"));
const AcceptInvitePage = React.lazy(
  () => import("./features/auth/components/AcceptInvitePage")
);
const ResetPasswordPage = React.lazy(
  () => import("./features/auth/components/ResetPasswordPage")
);
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage"));
const SupportPage = React.lazy(() => import("./pages/SupportPage"));
const AccountDeletionPage = React.lazy(() => import("./pages/AccountDeletionPage"));

function App() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);

  useEffect(() => {
    let disposed = false;

    // El arranque se resuelve SOLO con la sesión persistida localmente: sin
    // esperar ninguna petición de red la UI aparece al instante (incluso sin
    // conexión). La validez real del token la comprueban las propias queries
    // (RLS devuelve 401 y el refresh automático emite SIGNED_OUT si falla).
    supabase.auth.getSession()
      .then(({ data }) => {
        if (disposed) return;
        setSession(data.session);
        setIsLoadingSession(false);
      })
      .catch((error) => {
        logErrorToConsole(error, { module: 'App', operation: 'getSession' });
        if (!disposed) setIsLoadingSession(false);
      });

    // IMPORTANTE: este callback debe ser SÍNCRONO. Llamar métodos de auth
    // (getUser, signOut, ...) aquí dentro puede deadlockear el lock interno
    // de supabase-js y dejar la app congelada en el spinner de arranque.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (disposed) return;
      setSession(newSession);
      setIsLoadingSession(false);
      if (event === 'SIGNED_OUT') {
        // La caché guarda datos del usuario anterior (perfil, estaciones...):
        // se limpia aquí para cubrir TODA vía de cierre de sesión, no solo el
        // botón "Cerrar Sesión" (expiración, revocación, otra pestaña...).
        queryClient.clear();
        void offlineQueryPersister.removeClient();
        void unsubscribeLocalWebPush();
      }
    });

    return () => {
      disposed = true;
      subscription.unsubscribe();
    };
  }, [queryClient]);

  if (isLoadingSession) {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center">
        <Loading message="Iniciando sesión..." />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ErrorHandlerProvider>
        <BrowserRouter>
          <Suspense
            fallback={
              <div className="flex min-h-[100dvh] items-center justify-center bg-background">
                <Loading message="Cargando página..." />
              </div>
            }
          >
            <div className="min-h-screen bg-background">
              <Routes>
                {/* Ruta pública especial: el invitado llega desde el correo sin
                    sesión previa; supabase-js establece la sesión desde el enlace
                    y la página permite fijar la contraseña. Debe resolverse antes
                    del condicional de sesión para no ser capturada por el catch-all
                    de login ni por NotFound. */}
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                {/* Ruta pública del enlace de recuperación de contraseña
                    (resetPasswordForEmail redirige aquí). Igual que
                    /accept-invite, debe resolverse antes del condicional. */}
                <Route path="/auth/reset" element={<ResetPasswordPage />} />
                <Route path="/privacidad" element={<PrivacyPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/soporte" element={<SupportPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/integrations/help" element={<IntegrationHelpPage />} />
                {import.meta.env.DEV && <Route path="/integrations/lab" element={<IntegrationLabPage />} />}
                <Route path="/eliminar-cuenta" element={<AccountDeletionPage />} />
                <Route path="/delete-account" element={<AccountDeletionPage />} />
                {!session ? (
                  <>
                    <Route path="/login" element={<Auth />} />
                    <Route path="*" element={<Navigate to="/login" replace />} />
                  </>
                ) : (
                  <>
                    <Route path="/login" element={<Navigate to="/" replace />} />
                    <Route
                      path="/"
                      element={
                        <RequireAuth>
                          <DashboardLayout />
                        </RequireAuth>
                      }
                    >
                      <Route index element={<OverviewPage />} />
                      <Route path="network" element={<NetworkPage />} />
                      <Route path="climate" element={<ClimatePage />} />
                      <Route path="analysis" element={<AnalysisPage />} />
                      <Route path="alerts" element={<AlertsPage />} />
                      <Route path="registration" element={
                        <RequirePermission permissionCheck={(permissions) => permissions.canManageRegistration()}>
                          <RegistrationPage />
                        </RequirePermission>
                      } />
                      <Route path="actuators" element={
                        <RequirePermission permissionCheck={(permissions) => permissions.canManageActuators()}>
                          <ActuatorsPage />
                        </RequirePermission>
                      } />
                      <Route path="rules" element={
                        <RequirePermission permissionCheck={(permissions) => permissions.canManageAlertRules()}>
                          <RulesPage />
                        </RequirePermission>
                      } />
                      {/* FEATURES FUTURAS (ocultas del menú vía config/featureFlags.ts).
                          Se MANTIENEN las rutas/páginas: NO son código muerto.
                          Reactivar accesos poniendo los flags FEATURES.* en true.
                          Con el flag apagado la ruta NO se registra: así tampoco
                          son accesibles tecleando la URL directamente. */}
                      {FEATURES.crops && <Route path="crops" element={<CropsPage />} />}
                      {FEATURES.cameras && <Route path="devices" element={<DevicesPage />} />}
                      {FEATURES.imageAnalysis && (
                        <Route path="image-analysis" element={<ImageAnalysisPage />} />
                      )}
                      <Route path="system-settings" element={
                        <RequirePermission permissionCheck={(permissions) => permissions.canManageSystemSettings()}>
                          <SystemSettingsPage />
                        </RequirePermission>
                      } />
                      <Route path="users-management" element={
                        <RequirePermission permissionCheck={(permissions) => permissions.canManageStationMembers()}>
                          <UserManagementPage />
                        </RequirePermission>
                      } />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="integrations" element={<IntegrationsPage />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                  </>
                )}
              </Routes>
            </div>
          </Suspense>
          {/* Fuera del Suspense: mientras llega el chunk de una página el árbol
              suspendido no se pinta, y un aviso emitido en ese instante (p. ej.
              un error de inicio de sesión) se perdía sin mostrarse. */}
          <Toaster />
        </BrowserRouter>
      </ErrorHandlerProvider>
    </ErrorBoundary>
  );
}

export default App;
